import React, { useEffect, useState, useContext, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Button, 
  Container, 
  Typography, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  RocketLaunch,
  AccountCircle,
  ExitToApp,
  Dashboard as DashboardIcon,
  PlayArrow,
  Pause,
  Visibility,
  VisibilityOff,
  Star,
  StarBorder
} from '@mui/icons-material';
import { AuthContext } from '../contexts/AuthContext';
import PLMVisualization from '../components/PLMVisualization';

// Main EEvolveLanding Component
const EEvolveLanding = () => {
  const [loading, setLoading] = useState(true);
  const [cookieConsent, setCookieConsent] = useState(false);
  const [showCookieDialog, setShowCookieDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { isAuthenticated, user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2500);

    // Check if user has already given cookie consent
    const hasConsent = localStorage.getItem('cookieConsent');
    if (!hasConsent) {
      setTimeout(() => setShowCookieDialog(true), 3000);
    }

    return () => clearTimeout(timer);
  }, []);

  const handleCookieAccept = () => {
    localStorage.setItem('cookieConsent', 'true');
    setCookieConsent(true);
    setShowCookieDialog(false);
  };

  const handleCookieReject = () => {
    localStorage.setItem('cookieConsent', 'false');
    setCookieConsent(false);
    setShowCookieDialog(false);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
    navigate('/');
  };

  const handleDashboard = () => {
    handleMenuClose();
    navigate('/ROIAnalyzer');
  };

  if (loading) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: '#050518',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          color: '#00f3ff',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            width: '80px',
            height: '80px',
            border: '3px solid rgba(0, 243, 255, 0.3)',
            borderRadius: '50%',
            position: 'relative',
            margin: '0 auto 2rem',
            animation: 'spin 1.5s linear infinite',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
            '&:before': {
              content: '""',
              position: 'absolute',
              top: '-3px',
              left: '-3px',
              right: '-3px',
              bottom: '-3px',
              border: '3px solid transparent',
              borderTopColor: '#00f3ff',
              borderRadius: '50%',
              animation: 'spin 2s linear infinite',
            },
          }}
        />
        <Typography variant="h6" sx={{ mt: 2 }}>
          INITIALIZING eEvolve.ai EXPERIENCE
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Loading next-generation interface
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      background: '#0a0a2a', 
      color: '#e0e0ff',
      minHeight: '100vh',
      fontFamily: '"Exo 2", sans-serif',
      overflowX: 'hidden',
      position: 'relative',
    }}>
      {/* Background particles */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: -1,
          backgroundImage: `
            radial-gradient(circle at 20% 30%, rgba(150, 0, 255, 0.1) 0%, transparent 30%),
            radial-gradient(circle at 80% 70%, rgba(0, 243, 255, 0.1) 0%, transparent 30%)
          `,
        }}
      />
      
      {/* Header */}
      <Box
        component="header"
        sx={{
          background: 'rgba(10, 10, 42, 0.8)',
          backdropFilter: 'blur(10px)',
          padding: '1rem 0',
          position: 'fixed',
          width: '100%',
          top: 0,
          zIndex: 1000,
          boxShadow: '0 5px 20px rgba(0, 243, 255, 0.2)',
          borderBottom: '1px solid rgba(0, 243, 255, 0.3)',
        }}
      >
        <Container sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Box
              sx={{
                width: '40px',
                height: '40px',
                background: 'linear-gradient(45deg, #0a0a2a, #050518)',
                borderRadius: '10px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box
                component="i"
                className="fas fa-infinity"
                sx={{ fontSize: '1.5rem', color: '#00f3ff', zIndex: 2 }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  width: '4px',
                  height: '4px',
                  background: '#ff00c8',
                  borderRadius: '50%',
                  opacity: 0.7,
                  top: '5px',
                  left: '5px',
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 0.5, transform: 'scale(1)' },
                    '50%': { opacity: 1, transform: 'scale(1.05)' },
                    '100%': { opacity: 0.5, transform: 'scale(1)' },
                  },
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  width: '4px',
                  height: '4px',
                  background: '#ff00c8',
                  borderRadius: '50%',
                  opacity: 0.7,
                  bottom: '5px',
                  right: '5px',
                  animation: 'pulse 2s infinite 0.5s',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  width: '4px',
                  height: '4px',
                  background: '#ff00c8',
                  borderRadius: '50%',
                  opacity: 0.7,
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  animation: 'pulse 2s infinite 1s',
                }}
              />
            </Box>
            <Typography
              variant="h1"
              sx={{
                fontFamily: '"Orbitron", sans-serif',
                fontSize: '1.8rem',
                fontWeight: 700,
                background: 'linear-gradient(45deg, #00f3ff, #9600ff)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 0 15px rgba(0, 243, 255, 0.5)',
              }}
            >
              eEvolve.ai
            </Typography>
          </Box>
          
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: '1.5rem' }}>
            <Button 
              component={Link} 
              to="/dashboard" 
              sx={{ 
                color: '#e0e0ff', 
                textDecoration: 'none',
                position: 'relative',
                overflow: 'hidden',
                '&:before': {
                  content: 'attr(data-hover)',
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  background: 'linear-gradient(45deg, #00f3ff, #ff00c8)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  transition: 'transform 0.3s ease',
                },
                '&:hover:before': {
                  transform: 'translateY(-100%)',
                },
              }}
              data-hover="Dashboard"
            >
              Dashboard
            </Button>
            <Button 
              component={Link} 
              to="/services" 
              sx={{ 
                color: '#e0e0ff', 
                textDecoration: 'none',
                position: 'relative',
                overflow: 'hidden',
                '&:before': {
                  content: 'attr(data-hover)',
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                background: 'linear-gradient(45deg, #00f3ff, #ff00c8)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                transition: 'transform 0.3s ease',
              },
              '&:hover:before': {
                transform: 'translateY(-100%)',
              },
            }}
            data-hover="Services"
          >
            Services
          </Button>
          <Button 
            component={Link} 
            to="/scenarios" 
            sx={{ 
              color: '#e0e0ff', 
              textDecoration: 'none',
              position: 'relative',
              overflow: 'hidden',
              '&:before': {
                content: 'attr(data-hover)',
                position: 'absolute',
                top: '100%',
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: 'linear-gradient(45deg, #00f3ff, #ff00c8)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                transition: 'transform 0.3s ease',
              },
              '&:hover:before': {
                transform: 'translateY(-100%)',
              },
            }}
            data-hover="Scenarios"
          >
            Scenarios
          </Button>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isAuthenticated ? (
            <>
              <Button
                onClick={handleMenuOpen}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#00f3ff',
                  textTransform: 'none',
                  fontSize: '1rem',
                  '&:hover': {
                    background: 'rgba(0, 243, 255, 0.1)',
                  },
                }}
              >
                <Avatar 
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    bgcolor: 'primary.main',
                    fontSize: '0.9rem'
                  }}
                >
                  {user?.email ? user.email.charAt(0).toUpperCase() : 'U'}
                </Avatar>
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                  {user?.email ? user.email.split('@')[0] : 'User'}
                </Box>
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: {
                    bgcolor: '#0a0a2a',
                    color: '#e0e0ff',
                    border: '1px solid rgba(0, 243, 255, 0.3)',
                    mt: 1,
                    minWidth: '200px',
                  }
                }}
              >
                <MenuItem onClick={handleLogout}>
                  <ExitToApp sx={{ mr: 2, fontSize: '1.2rem' }} />
                  Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Button
              component={Link}
              to="/login"
              sx={{
                background: 'transparent',
                color: '#00f3ff',
                border: '2px solid #00f3ff',
                boxShadow: '0 0 10px rgba(0, 243, 255, 0.3)',
                borderRadius: '50px',
                padding: '0.5rem 1.5rem',
                textTransform: 'uppercase',
                fontWeight: 600,
                letterSpacing: '1px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'rgba(0, 243, 255, 0.1)',
                  boxShadow: '0 0 20px rgba(0, 243, 255, 0.5)',
                },
              }}
            >
              Sign In
            </Button>
          )}
        </Box>
      </Container>
    </Box>

    {/* Hero Section */}
    <Box
      component="section"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        paddingTop: '80px',
        overflow: 'hidden',
      }}
    >
      <Container sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
        gap: '3rem', 
        alignItems: 'center' 
      }}>
        <Box>
          <Typography
            variant="h2"
            sx={{
              fontFamily: '"Orbitron", sans-serif',
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              marginBottom: '1.5rem',
              background: 'linear-gradient(45deg, #00f3ff, #ff00c8)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: '0 0 20px rgba(0, 243, 255, 0.3)',
              position: 'relative',
            }}
          >
            Transform Your Business Through AI
          </Typography>
          <Typography variant="body1" sx={{ marginBottom: '1.5rem', fontSize: '1.1rem', color: '#a0a0ff' }}>
            eEvolve.ai delivers intelligent transformation with emotional intelligence, creating solutions that understand your business as deeply as you do.
          </Typography>
          <Box sx={{ display: 'flex', gap: '1rem', marginTop: '2rem', flexWrap: 'wrap' }}>

<Button
  component={Link}
  to="/aisolarch"
  sx={{
    background: 'transparent',
    color: '#00f3ff',
    border: '2px solid #00f3ff',
    boxShadow: '0 0 10px rgba(0, 243, 255, 0.3)',
    padding: '1rem 2rem',
    borderRadius: '50px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: 'rgba(0, 243, 255, 0.1)',
      boxShadow: '0 0 20px rgba(0, 243, 255, 0.5)',
      transform: 'translateY(-2px)',
    },
  }}
>
  AI Solutions Architect
</Button>
<Button
  component={Link}
  to="/services"
  sx={{
    background: 'transparent',
    color: '#00f3ff',
    border: '2px solid #00f3ff',
    boxShadow: '0 0 10px rgba(0, 243, 255, 0.3)',
    padding: '1rem 2rem',
    borderRadius: '50px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: 'rgba(0, 243, 255, 0.1)',
      boxShadow: '0 0 20px rgba(0, 243, 255, 0.5)',
      transform: 'translateY(-2px)',
    },
  }}
>
  Explore Solutions
</Button>
            <Button
              component={Link}
              to="/register"
              sx={{
                background: 'linear-gradient(45deg, #00f3ff, #9600ff)',
                color: 'white',
                boxShadow: '0 5px 15px rgba(150, 0, 255, 0.4)',
                padding: '1rem 2rem',
                borderRadius: '50px',
                fontWeight: 600,                 textTransform: 'uppercase',
                letterSpacing: '1px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 10px 25px rgba(150, 0, 255, 0.6)',
                },
              }}
            >
              Begin Your Evolution
            </Button>

          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Box sx={{ width: '300px', height: '300px', position: 'relative' }}>
            {/* AI Brain Visualization */}
            <Box
              sx={{
                position: 'absolute',
                borderRadius: '50%',
                background: 'rgba(0, 243, 255, 0.1)',
                border: '2px solid rgba(0, 243, 255, 0.5)',
                boxShadow: '0 0 20px rgba(0, 243, 255, 0.3)',
                width: '120px',
                height: '120px',
                top: '50px',
                left: '30px',
                animation: 'pulse 3s infinite',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                borderRadius: '50%',
                background: 'rgba(0, 243, 255, 0.1)',
                border: '2px solid rgba(0, 243, 255, 0.5)',
                boxShadow: '0 0 20px rgba(0, 243, 255, 0.3)',
                width: '100px',
                height: '100px',
                top: '80px',
                right: '40px',
                animation: 'pulse 3s infinite 0.5s',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                borderRadius: '50%',
                background: 'rgba(0, 243, 255, 0.1)',
               border: '2px solid rgba(0, 243, 255, 0.5)',
                boxShadow: '0 0 20px rgba(0, 243, 255, 0.3)',
                width: '140px',
                height: '140px',
                bottom: '50px',
                left: '50%',
                transform: 'translateX(-50%)',
                animation: 'pulse 3s infinite 1s',
              }}
            />
            
            {/* Neural Connections */}
            <Box
              sx={{
                position: 'absolute',
                background: 'linear-gradient(to right, transparent, #00f3ff, transparent)',
                height: '2px',
                width: '100px',
                top: '80px',
                left: '150px',
                transform: 'rotate(30deg)',
                animation: 'neuralPulse 3s infinite 0.2s',
                '@keyframes neuralPulse': {
                  '0%': { opacity: 0, width: 0 },
                  '50%': { opacity: 1, width: '100%' },
                  '100%': { opacity: 0, width: 0 },
                },
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                background: 'linear-gradient(to right, transparent, #00f3ff, transparent)',
                height: '2px',
                width: '120px',
                top: '150px',
                left: '90px',
                transform: 'rotate(-15deg)',
                animation: 'neuralPulse 3s infinite 0.7s',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                background: 'linear-gradient(to right, transparent, #00f3ff, transparent)',
                height: '2px',
                width: '110px',
                bottom: '100px',
                right: '100px',
                transform: 'rotate(20deg)',
                animation: 'neuralPulse 3s infinite 1.2s',
              }}
            />
          </Box>
        </Box>
      </Container>
      
      {/* Scroll Indicator */}
      <Box
        sx={{
          position: 'absolute',
          bottom: '30px',
          left: '50%',
          transform: 'translateX(-50%)',
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: '2px',
            height: '40px',
            background: 'linear-gradient(to bottom, #00f3ff, transparent)',
            margin: '0 auto 10px',
            animation: 'scrollLine 2s infinite',
            '@keyframes scrollLine': {
              '0%': { height: 0, opacity: 0 },
              '50%': { height: '40px', opacity: 1 },
              '100%': { height: 0, opacity: 0, transform: 'translateY(20px)' },
            },
          }}
        />
        <Typography variant="body2">SCROLL TO EXPLORE</Typography>
      </Box>
    </Box>

    {/* The Lumina Thread Visualization Section */}


      {/* Empathy Section */}
      <Box
        component="section"
        sx={{ padding: { xs: '4rem 0', md: '8rem 0' }, position: 'relative' }}
      >
        <Container>
          <Box sx={{ textAlign: 'center', marginBottom: '5rem' }}>
            <Typography variant="h2" sx={{ marginBottom: '1.5rem' }}>
              We Understand Your Journey
            </Typography>
            <Typography variant="body1" sx={{ color: '#a0a0ff' }}>
              True transformation begins with empathy - we listen, understand, and evolve with you
            </Typography>
          </Box>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, 
            gap: '2.5rem' 
          }}>
            {/* Emotionally Intelligent AI Card */}
            <Box
              sx={{
                background: 'rgba(10, 10, 42, 0.5)',
                borderRadius: '15px',
                padding: '2.5rem',
                textAlign: 'center',
                transition: 'all 0.4s ease',
                border: '1px solid rgba(0, 243, 255, 0.2)',
                backdropFilter: 'blur(5px)',
                '&:hover': {
                  transform: 'translateY(-10px)',
                  borderColor: '#00f3ff',
                  boxShadow: '0 10px 30px rgba(0, 243, 255, 0.2)',
                },
              }}
            >
              <Box
                sx={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'rgba(0, 243, 255, 0.1)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  margin: '0 auto 1.5rem',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    border: '2px solid #00f3ff',
                    animation: 'pulseRing 2s infinite',
                    opacity: 0,
                    '@keyframes pulseRing': {
                      '0%': { transform: 'scale(1)', opacity: 1 },
                      '100%': { transform: 'scale(1.5)', opacity: 0 },
                    },
                  }}
                />
                <Box component="i" className="fas fa-heart" sx={{ fontSize: '2rem', color: '#00f3ff', zIndex: 2 }} />
              </Box>
              <Typography variant="h3" sx={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#00f3ff' }}>
                Emotionally Intelligent AI
              </Typography>
              <Typography variant="body1" sx={{ color: '#a0a0ff' }}>
                Our systems don't just process data - they understand context, nuance, and human emotion in business decisions.
              </Typography>
            </Box>
            
            {/* Partnership Card */}
            <Box
              sx={{
                background: 'rgba(10, 10, 42, 0.5)',
                borderRadius: '15px',
                padding: '2.5rem',
                textAlign: 'center',
                transition: 'all 0.4s ease',
                border: '1px solid rgba(0, 243, 255, 0.2)',
                backdropFilter: 'blur(5px)',
                '&:hover': {
                  transform: 'translateY(-10px)',
                  borderColor: '#00f3ff',
                  boxShadow: '0 10px 30px rgba(0, 243, 255, 0.2)',
                },
              }}
            >
              <Box
                sx={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'rgba(0, 243, 255, 0.1)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  margin: '0 auto 1.5rem',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    border: '2px solid #00f3ff',
                    animation: 'pulseRing 2s infinite',
                    opacity: 0,
                  }}
                />
                <Box component="i" className="fas fa-hands-helping" sx={{ fontSize: '2rem', color: '#00f3ff', zIndex: 2 }} />
              </Box>
              <Typography variant="h3" sx={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#00f3ff' }}>
                Partnership, Not Provision
              </Typography>
              <Typography variant="body1" sx={{ color: '#a0a0ff' }}>
                We walk alongside you throughout your transformation journey, adapting to your unique challenges and goals.
              </Typography>
            </Box>
            
            {/* Growth Card */}
            <Box
              sx={{
                background: 'rgba(10, 10, 42, 0.5)',
                borderRadius: '15px',
                padding: '2.5rem',
                textAlign: 'center',
                transition: 'all 0.4s ease',
                border: '1px solid rgba(0, 243, 255, 0.2)',
                backdropFilter: 'blur(5px)',
                '&:hover': {
                  transform: 'translateY(-10px)',
                  borderColor: '#00f3ff',
                  boxShadow: '0 10px 30px rgba(0, 243, 255, 0.2)',
                },
              }}
            >
              <Box
                sx={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'rgba(0, 243, 255, 0.1)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  margin: '0 auto 1.5rem',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    border: '2px solid #00f3ff',
                    animation: 'pulseRing 2s infinite',
                    opacity: 0,
                  }}
                />
                <Box component="i" className="fas fa-seedling" sx={{ fontSize: '2rem', color: '#00f3ff', zIndex: 2 }} />
              </Box>
              <Typography variant="h3" sx={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#00f3ff' }}>
                Growth With Purpose
              </Typography>
              <Typography variant="body1" sx={{ color: '#a0a0ff' }}>
                Our solutions are designed to evolve with your business, ensuring sustainable growth and continuous innovation.
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Call to Action */}
      <Box
        component="section"
        sx={{
          background: 'rgba(5, 5, 24, 0.7)',
          padding: { xs: '4rem 0', md: '8rem 0' },
          textAlign: 'center',
        }}
      >
        <Container>
          <Typography variant="h2" sx={{ marginBottom: '1.5rem' }}>
            Ready to Transform Your Business?
          </Typography>
          <Typography variant="body1" sx={{ marginBottom: '2.5rem', color: '#a0a0ff' }}>
            Join forward-thinking companies that have already started their AI evolution journey
          </Typography>
          <Button
            component={Link}
            to={isAuthenticated ? "/ROIAnalyzer" : "/register"}
            sx={{
              background: 'linear-gradient(45deg, #00f3ff, #9600ff)',
              color: 'white',
              boxShadow: '0 5px 15px rgba(150, 0, 255, 0.4)',
              padding: '1rem 2.5rem',
              borderRadius: '50px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontSize: '1.1rem',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: '0 10px 25px rgba(150, 0, 255, 0.6)',
              },
            }}
          >
            <RocketLaunch sx={{ mr: 1 }} />
            {isAuthenticated ? 'Go to Dashboard' : 'Start Your Evolution'}
          </Button>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          background: '#050518',
          padding: '4rem 0 2rem',
          borderTop: '1px solid rgba(0, 243, 255, 0.2)',
        }}
      >
        <Container>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, 
            gap: '3rem', 
            marginBottom: '3rem' 
          }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1rem' }}>
                <Box
                  sx={{
                    width: '30px',
                    height: '30px',
                    background: 'linear-gradient(45deg, #0a0a2a, #050518)',
                    borderRadius: '10px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Box component="i" className="fas fa-infinity" sx={{ fontSize: '1rem', color: '#00f3ff' }} />
                </Box>
                <Typography variant="h2" sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#00f3ff' }}>
                  eEvolve.ai
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#a0a0ff' }}>
                Transforming businesses through AI-powered evolution and emotionally intelligent technology solutions.
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="h3" sx={{ fontSize: '1.2rem', marginBottom: '1.5rem', color: '#00f3ff' }}>
                Solutions
              </Typography>
              <Box component="ul" sx={{ listStyle: 'none', padding: 0 }}>
                <li><Button component={Link} to="/analyze" sx={{ color: '#a0a0ff', textDecoration: 'none', fontSize: '0.9rem', p: 0 }}>AI-Powered Consulting</Button></li>
                <li><Button component={Link} to="/analyze" sx={{ color: '#a0a0ff', textDecoration: 'none', fontSize: '0.9rem', p: 0 }}>ROI Optimization</Button></li>
                <li><Button component={Link} to="/GCCTransform" sx={{ color: '#a0a0ff', textDecoration: 'none', fontSize: '0.9rem', p: 0 }}>GCC Setup & Evolution</Button></li>
                <li><Button component={Link} to="/analyze" sx={{ color: '#a0a0ff', textDecoration: 'none', fontSize: '0.9rem', p: 0 }}>PLM Integration</Button></li>
              </Box>
            </Box>
            
            <Box>
              <Typography variant="h3" sx={{ fontSize: '1.2rem', marginBottom: '1.5rem', color: '#00f3ff' }}>
                Resources
              </Typography>
              <Box component="ul" sx={{ listStyle: 'none', padding: 0 }}>
                <li><Button component={Link} to="/blog" sx={{ color: '#a0a0ff', textDecoration: 'none', fontSize: '0.9rem', p: 0 }}>Blog</Button></li>
                <li><Button component={Link} to="/case-studies" sx={{ color: '#a0a0ff', textDecoration: 'none', fontSize: '0.9rem', p: 0 }}>Case Studies</Button></li>
                <li><Button component={Link} to="/whitepapers" sx={{ color: '#a0a0ff', textDecoration: 'none', fontSize: '0.9rem', p: 0 }}>Whitepapers</Button></li>
                <li><Button component={Link} to="/webinars" sx={{ color: '#a0a0ff', textDecoration: 'none', fontSize: '0.9rem', p: 0 }}>Webinars</Button></li>
              </Box>
            </Box>
            
            <Box>
              <Typography variant="h3" sx={{ fontSize: '1.2rem', marginBottom: '1.5rem', color: '#00f3ff' }}>
                Connect
              </Typography>
              <Box component="ul" sx={{ listStyle: 'none', padding: 0 }}>
                <li><Button component={Link} to="/contact" sx={{ color: '#a0a0ff', textDecoration: 'none', fontSize: '0.9rem', p: 0 }}>Contact Us</Button></li>

<li>
  <Button 
    onClick={() => {
      if (isAuthenticated) {
        navigate('/dashboard?setSupportDialogOpen(true)');
      } else {
        navigate('/login');
      }
    }}
    sx={{ color: '#a0a0ff', textDecoration: 'none', fontSize: '0.9rem', p: 0 }}
  >
    Support
  </Button>
</li>

                <li><Button component={Link} to="/careers" sx={{ color: '#a0a0ff', textDecoration: 'none', fontSize: '0.9rem', p: 0 }}>Careers</Button></li>
                <li><Button component={Link} to="/partners" sx={{ color: '#a0a0ff', textDecoration: 'none', fontSize: '0.9rem', p: 0 }}>Partners</Button></li>

              </Box>
            </Box>
          </Box>
         
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            paddingTop: '2rem', 
            borderTop: '1px solid rgba(0, 243, 255, 0.1)',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: '1rem', md: 0 },
          }}>
            <Typography variant="body2" sx={{ color: '#a0a0ff' }}>
              © 2023 eEvolve.ai. All rights reserved.
            </Typography>
            <Box sx={{ display: 'flex', gap: '1.5rem' }}>
              <Button component={Link} to="/terms" sx={{ color: '#a0a0ff', fontSize: '0.9rem' }}>Terms of Service</Button>
              <Button component={Link} to="/privacy" sx={{ color: '#a0a0ff', fontSize: '0.9rem' }}>Privacy Policy</Button>
              <Button component={Link} to="/refund-policy" sx={{ color: '#a0a0ff', fontSize: '0.9rem' }}>Refund Policy</Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Cookie Consent Dialog */}
      <Dialog 
        open={showCookieDialog} 
        onClose={handleCookieReject}
        PaperProps={{
          sx: {
            bgcolor: '#0a0a2a',
            color: '#e0e0ff',
            border: '1px solid rgba(0, 243, 255, 0.3)',
          }
        }}
      >
        <DialogTitle sx={{ color: '#00f3ff', borderBottom: '1px solid rgba(0, 243, 255, 0.2)' }}>
          Cookie Consent
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 2 }}>
            We use cookies to enhance your experience, analyze site traffic, and for marketing purposes. 
            By continuing to use our site, you consent to our use of cookies.
          </Typography>
          <Typography variant="caption" sx={{ color: '#a0a0ff' }}>
            For more information, please see our <Link to="/privacy" style={{ color: '#00f3ff' }}>Privacy Policy</Link>.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(0, 243, 255, 0.2)', p: 2 }}>
          <Button 
            onClick={handleCookieReject} 
            sx={{ 
              color: '#a0a0ff',
              '&:hover': {
                color: '#00f3ff',
                bgcolor: 'rgba(0, 243, 255, 0.1)'
              }
            }}
          >
            Reject
          </Button>
          <Button 
            onClick={handleCookieAccept} 
            variant="contained"
            sx={{
              background: 'linear-gradient(45deg, #00f3ff, #9600ff)',
              '&:hover': {
                background: 'linear-gradient(45deg, #00d0ff, #7a00cc)',
              }
            }}
          >
            Accept
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EEvolveLanding;
